/** Student CSV import modal. */
import * as React from 'react';
import { Upload, Download, Check } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { BaseModal } from '@/components/common/modals/BaseModal';
import { Button } from '@/components/forms/Button';
import { FileDropzone } from '@/components/common/FileDropzone';
import { importStudentsCsv } from '@/services/inperson/students.service';
import { downloadStudentImportTemplate } from '@/services/files.service';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import {
  STUDENT_FORM_QUERY_KEYS,
  STUDENT_IMPORT_TEMPLATE_FILENAME,
} from '@/components/students/student-configs';
import { resolveStudentImportToasts } from '@/components/students/inperson/student-form-utils';
import type { ImportCsvError } from '@/types/inperson/student.types';

/** Max row errors listed in the modal (full list stays in the console). */
const MAX_DISPLAYED_IMPORT_ERRORS = 50;

/** One rejected CSV row in the import error list. */
function renderImportErrorRow(error: ImportCsvError): React.ReactNode {
  return (
    <li key={`${error.rowNumber}-${error.reason}`}>
      Ligne {error.rowNumber} : {getErrorMessage(error.reason)}
    </li>
  );
}

/** Alert listing rejected import rows (capped). */
function renderImportErrorsAlert(importErrors: ImportCsvError[]): React.ReactNode {
  if (importErrors.length === 0) return null;
  const visible = importErrors.slice(0, MAX_DISPLAYED_IMPORT_ERRORS);
  const overflowCount = importErrors.length - MAX_DISPLAYED_IMPORT_ERRORS;
  return (
    <div
      role="alert"
      className="p-3 bg-red-50 border border-red-200 rounded-lg flex flex-col gap-2"
    >
      <p className="text-sm font-medium text-red-700">
        {importErrors.length} ligne(s) non importée(s) — corrigez-les puis réimportez le fichier :
      </p>
      <ul className="max-h-40 overflow-y-auto text-xs text-red-700 list-disc pl-5 flex flex-col gap-1">
        {visible.map(renderImportErrorRow)}
      </ul>
      {overflowCount > 0 && (
        <p className="text-xs text-red-700">… et {overflowCount} autre(s) ligne(s).</p>
      )}
    </div>
  );
}

/** Props for {@link ImportStudentsModal}. */
interface ImportStudentsModalProps {
  open: boolean;
  onClose: () => void;
  onImportStart?: () => void;
  onImportEnd?: () => void;
}

/** Student CSV import modal. */
export const ImportStudentsModal: React.FC<Readonly<ImportStudentsModalProps>> = ({
  open,
  onClose,
  onImportStart,
  onImportEnd,
}) => {
  const queryClient = useQueryClient();
  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [isImporting, setIsImporting] = React.useState(false);
  const [importErrors, setImportErrors] = React.useState<ImportCsvError[]>([]);
  /** Bumped to reset the dropzone after an import that reported row errors. */
  const [dropzoneKey, setDropzoneKey] = React.useState(0);

  const handleDownloadTemplate = async () => {
    try {
      await downloadStudentImportTemplate(STUDENT_IMPORT_TEMPLATE_FILENAME);
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  /** Upload the file, toast the summary, list row errors, then invalidate the student list. */
  const handleImport = async () => {
    if (!selectedFile) {
      toast.error(getErrorMessage('csvRequired'));
      return;
    }

    setIsImporting(true);
    onImportStart?.();

    try {
      const result = await importStudentsCsv(selectedFile);
      if (result.errors.length > 0) {
        console.warn('Import errors:', result.errors);
      }
      for (const item of resolveStudentImportToasts(result)) {
        toast[item.kind](item.message);
      }
      await queryClient.invalidateQueries({ queryKey: STUDENT_FORM_QUERY_KEYS.usersStudent });
      setSelectedFile(null);
      if (result.errors.length > 0) {
        // Keep the modal open so the user can fix the rejected rows and re-import them.
        setImportErrors(result.errors);
        setDropzoneKey((key) => key + 1);
      } else {
        setImportErrors([]);
        onClose();
      }
    } catch (error) {
      console.error('Error importing CSV:', error);
      toast.error(getErrorMessage(error));
    } finally {
      setIsImporting(false);
      onImportEnd?.();
    }
  };

  /** Block close while import is running so we never drop an in-flight request. */
  const handleClose = () => {
    if (isImporting) return;
    setSelectedFile(null);
    setImportErrors([]);
    onClose();
  };

  return (
    <BaseModal
      open={open}
      onClose={handleClose}
      size="lg"
      closeOnOutsideClick={!isImporting}
      closeOnEscape={!isImporting}
      header={{
        icon: Upload,
        iconSize: 'md',
        title: 'Importer des élèves',
        subtitle: 'Téléchargez le modèle Excel, remplissez-le et importez-le',
      }}
      footer={
        <div className="flex items-center justify-end gap-3 w-full">
          <Button preset="modal-cancel" onClick={handleClose} disabled={isImporting}>
            Annuler
          </Button>
          <Button
            variant="primary"
            size="md"
            icon={isImporting ? undefined : Check}
            iconPosition="left"
            onClick={handleImport}
            disabled={!selectedFile || isImporting}
            loading={isImporting}
          >
            {isImporting ? 'Import en cours...' : 'Importer'}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <p className="text-sm font-medium text-[var(--color-1-nuance-1)]">
            1. Téléchargez le modèle Excel
          </p>
          <Button
            variant="outline"
            size="md"
            icon={Download}
            iconPosition="left"
            onClick={handleDownloadTemplate}
            disabled={isImporting}
            fullWidth
          >
            Télécharger le modèle Excel
          </Button>
        </div>

        <div className="flex flex-col items-center gap-1.5 -mt-1">
          <span className="text-xs uppercase text-muted-foreground">Puis</span>
          <div className="w-full border-t border-[var(--color-1-nuance-3)]" />
        </div>

        <div className="flex flex-col gap-2">
          <p className="text-sm font-medium text-[var(--color-1-nuance-1)]">
            2. Importez votre fichier rempli (.xlsx ou .csv)
          </p>
          <FileDropzone
            key={dropzoneKey}
            onFileSelect={setSelectedFile}
            accept=".xlsx,.csv"
            maxSize={10 * 1024 * 1024}
            disabled={isImporting}
          />
        </div>

        <div className="p-3 bg-[color-mix(in_srgb,var(--color-2-nuance-1)_5%,transparent)] border border-[var(--color-2-nuance-1)]/20 rounded-lg">
          <p className="text-xs text-muted-foreground">
            <strong className="text-[var(--color-2-nuance-1)]">Champs obligatoires :</strong>{' '}
            Prénom, Nom (2 caractères conservés), Genre, Établissement et Niveau — comme dans le
            formulaire de création. Choisissez le genre, l’établissement et le niveau dans les
            listes déroulantes du modèle. La colonne « Nom affiché » est facultative : utilisez-la
            si deux élèves ont le même prénom et les mêmes 2 premières lettres de nom. Maximum 500
            élèves par fichier.
          </p>
        </div>

        {renderImportErrorsAlert(importErrors)}
      </div>
    </BaseModal>
  );
};
